import { Contact, ContactChannel } from './types/Contact';

export interface ResolvedContactName {
  firstName: string;
  lastName: string;
  displayName: string;
}

const GENERATED_CHANNEL_NAME = /^(WhatsApp|Telegram|Messenger|Instagram|TikTok)(\s|$)/i;
const PHONE_LIKE = /^[\s()+.-]*\+?[\d\s().-]+$/;

export const collectChannelValues = (
  values: Array<string | undefined | null>,
): string[] => {
  return values
    .map(value => value?.trim())
    .filter((value): value is string => Boolean(value));
};

export const collectContactChannelValues = (
  contact: Partial<Pick<Contact, 'phone' | 'whatsApp' | 'email' | 'channels'>>,
): string[] => {
  return collectChannelValues([
    contact.phone,
    contact.whatsApp,
    contact.email,
    ...(contact.channels ?? []).map(channel => channel.value),
  ]);
};

export const looksLikeChannelValue = (value?: string | null): boolean => {
  const trimmed = value?.trim();
  if (!trimmed) {
    return false;
  }

  if (trimmed.startsWith('@')) {
    return !/\s/.test(trimmed);
  }

  if (/^(webchat:|whatsapp:)/i.test(trimmed)) {
    return true;
  }

  if (GENERATED_CHANNEL_NAME.test(trimmed)) {
    return true;
  }

  if (trimmed.includes('@') && trimmed.includes('.')) {
    return true;
  }

  const digitCount = trimmed.match(/\p{Nd}/gu)?.length ?? 0;
  const letterCount = trimmed.match(/\p{L}/gu)?.length ?? 0;
  return digitCount >= 7 && (letterCount === 0 || PHONE_LIKE.test(trimmed));
};

export const sanitizePersonName = (
  value?: string | null,
  channelValues: Array<string | undefined | null> = [],
): string => {
  const trimmed = value?.trim() ?? '';
  if (!trimmed) {
    return '';
  }

  const excluded = new Set(collectChannelValues(channelValues).map(item => item.toLowerCase()));
  if (excluded.has(trimmed.toLowerCase()) || looksLikeChannelValue(trimmed)) {
    return '';
  }

  const kept = trimmed
    .split(/\s+/)
    .filter(token => token && !looksLikeChannelValue(token) && !excluded.has(token.toLowerCase()));

  if (kept.length === 0) {
    return '';
  }

  const joined = kept.join(' ');
  return looksLikeChannelValue(joined) || excluded.has(joined.toLowerCase()) ? '' : joined;
};

export const resolveContactName = (
  contact: Pick<Contact, 'name' | 'firstName' | 'lastName'>
    & Partial<Pick<Contact, 'phone' | 'whatsApp' | 'email' | 'channels'>>,
): ResolvedContactName => {
  const channelValues = collectContactChannelValues(contact);
  const firstName = sanitizePersonName(contact.firstName, channelValues);
  const lastName = sanitizePersonName(contact.lastName, channelValues);

  if (firstName || lastName) {
    const displayName = [firstName, lastName].filter(Boolean).join(' ').trim();
    return {
      firstName,
      lastName,
      displayName,
    };
  }

  const name = contact.name?.trim() ?? '';
  if (!name) {
    return {
      firstName: '',
      lastName: '',
      displayName: '',
    };
  }

  const sanitizedName = sanitizePersonName(name, channelValues);
  if (!sanitizedName) {
    return {
      firstName: '',
      lastName: '',
      displayName: '',
    };
  }

  const parts = sanitizedName.split(/\s+/).filter(Boolean);
  if (parts.length === 1) {
    return {
      firstName: parts[0],
      lastName: '',
      displayName: sanitizedName,
    };
  }

  return {
    firstName: parts[0],
    lastName: parts.slice(1).join(' '),
    displayName: sanitizedName,
  };
};

export const withResolvedContactName = (contact: Contact): Contact => {
  const resolved = resolveContactName(contact);

  return {
    ...contact,
    firstName: resolved.firstName || undefined,
    lastName: resolved.lastName || undefined,
    name: contact.name?.trim() || resolved.displayName,
  };
};

export const getContactPersonDisplayName = (
  contact: Pick<Contact, 'name' | 'firstName' | 'lastName'>
    & Partial<Pick<Contact, 'phone' | 'whatsApp' | 'email' | 'channels'>>,
): string => {
  return resolveContactName(contact).displayName;
};

export const getContactNameEmail = (contact: Pick<Contact, 'email'>): string => {
  const email = contact.email?.trim() ?? '';
  return email.includes('@') ? email : '';
};

export const personNameChannelError = (
  value: string,
  channels: Pick<ContactChannel, 'value'>[],
  extraValues: Array<string | undefined | null> = [],
): boolean => {
  const trimmed = value.trim();
  if (!trimmed) {
    return false;
  }

  return !sanitizePersonName(trimmed, [
    ...extraValues,
    ...channels.map(channel => channel.value),
  ]);
};
